import React, { useCallback, useEffect, useState } from 'react';
import { useCpPortal } from '../../hooks/useCpPortal';
import {
  Key, RefreshCw, CheckCircle2, Lock, Loader2, Copy, AlertTriangle, ShieldCheck, Eye, EyeOff,
} from 'lucide-react';

interface UatCreds {
  issued: boolean;
  issuedAt: string | null;
  signoffAt: string | null;
  apiKey: string | null;
}
interface ProdCreds {
  issued: boolean;
  issuedAt: string | null;
  gated: boolean;
  gateReasons: string[];
}
interface CredStatus {
  uat: UatCreds;
  prod: ProdCreds;
}
interface IssuedCreds {
  env: string;
  apiKey: string;
  apiSecret: string;
  issuedAt: string;
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard blocked */ }
      }}
      className="inline-flex items-center gap-1 rounded-lg border border-[var(--border)] px-2 py-1 text-xs hover:bg-[var(--muted)]"
    >
      {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export const CredentialsCenterOverlay: React.FC = () => {
  const { apiBase, authHeaders, isMirror } = useCpPortal();
  const [status, setStatus] = useState<CredStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<IssuedCreds | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [signoffDone, setSignoffDone] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/credentials`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load credentials');
      setStatus(data.credentials);
      // UAT sign-off is complete once uat_signoff_at is set, independent of the
      // production gate (prod stays gated until the float is funded, but that
      // must NOT re-surface the UAT sign-off UI). Previously this used
      // `!prod.gated`, which flipped back to "not signed off" when the float
      // was not yet funded — causing the sign-off banner to reappear.
      setSignoffDone(!!data.credentials.uat.signoffAt);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load credentials');
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const rotate = useCallback(async () => {
    if (isMirror) return;
      if (status?.uat.issued && !confirm('Rotating your sandbox credentials invalidates the current API key and secret. Continue?')) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await fetch(`${apiBase}/credentials/uat/rotate`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Rotation failed');
      setRevealed(data.credentials);
      setShowSecret(true);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Rotation failed');
    } finally {
      setBusy(false);
    }
  }, [apiBase, authHeaders, isMirror, status, load]);

  const signoff = useCallback(async () => {
    if (isMirror) return;
    if (!confirm('Confirm sandbox sign-off? This unlocks production credential issuance after float prefunding.')) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await fetch(`${apiBase}/credentials/uat/signoff`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Sign-off failed');
      setSignoffDone(true);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Sign-off failed');
    } finally {
      setBusy(false);
    }
  }, [apiBase, authHeaders, isMirror, load]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--muted-foreground)]" aria-hidden />
      </div>
    );
  }
  if (error) {
    return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-[var(--primary)]/10 p-2">
            <Key className="h-5 w-5 text-[var(--primary)]" aria-hidden />
          </div>
          <div>
            <h2 className="text-base font-semibold text-[var(--foreground)]">Credentials Center</h2>
            <p className="mt-1 text-sm text-[var(--muted-foreground)]">
              Your API key is shown below. Your API secret is revealed only once when issued or rotated —
              store it securely. If lost, rotate to issue a new secret.
            </p>
          </div>
        </div>
      </div>

      {/* Sandbox (Testing) — backend env key remains `uat` */}
      <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-[var(--foreground)]">Sandbox (Testing)</h3>
            {status?.uat.issued ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-800">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Issued
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                Not issued
              </span>
            )}
          </div>
          <button
            type="button"
            disabled={busy || isMirror}
            onClick={rotate}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--primary)] px-3 py-2 text-xs font-semibold text-[var(--primary-foreground)] transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <RefreshCw className="h-3.5 w-3.5" aria-hidden />}
            {status?.uat.issued ? 'Rotate sandbox credentials' : 'Issue sandbox credentials'}
          </button>
        </div>

        {status?.uat.issued && status.uat.apiKey && (
          <div className="mt-3 rounded-lg border border-[var(--border)] bg-[var(--muted)]/40 p-3">
            <p className="text-xs font-medium uppercase text-[var(--muted-foreground)]">API Key (identifier)</p>
            <div className="mt-1 flex items-center justify-between gap-2">
              <code className="truncate font-mono text-sm text-[var(--foreground)]">{status.uat.apiKey}</code>
              <CopyButton value={status.uat.apiKey} />
            </div>
            <p className="mt-3 text-xs text-[var(--muted-foreground)]">
              API Secret: hidden (reveal-once). Rotate to generate a new secret.
            </p>
          </div>
        )}

        {status?.uat.issued && !signoffDone && (
          <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <div className="flex items-start gap-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 text-blue-600" aria-hidden />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">Ready for sandbox sign-off?</p>
                <p className="mt-0.5 text-xs text-blue-800">
                  Once you have completed sandbox testing, confirm sign-off to unlock production credentials.
                </p>
                <button
                  type="button"
                  disabled={busy || isMirror}
                  onClick={signoff}
                  className="mt-2 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                >
                  Confirm sandbox sign-off
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Production */}
      <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-[var(--foreground)]">Production</h3>
          {status?.prod.gated ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700">
              <Lock className="h-3.5 w-3.5" aria-hidden /> Locked
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-800">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Unlocked
            </span>
          )}
        </div>
        {status?.prod.gated ? (
          <div className="mt-3 space-y-1">
            {status.prod.gateReasons.map((r) => (
              <p key={r} className="flex items-start gap-2 text-xs text-[var(--muted-foreground)]">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 text-amber-500" aria-hidden /> {r}
              </p>
            ))}
            <p className="text-xs text-[var(--muted-foreground)]">
              Production credentials are issued by MyMoolah after sandbox sign-off and production float prefunding.
            </p>
          </div>
        ) : (
          <p className="mt-2 text-xs text-[var(--muted-foreground)]">
            Contact MyMoolah to issue production credentials.
          </p>
        )}
      </div>

      {actionError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{actionError}</div>
      )}

      {/* One-time secret reveal modal */}
      {revealed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-amber-100 p-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" aria-hidden />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-[var(--foreground)]">Save your API secret now</h3>
                <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                  This is the only time your API secret will be shown. Copy it to a secure secrets manager now.
                  It cannot be retrieved again — rotate if you lose it.
                </p>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              <div className="rounded-lg border border-[var(--border)] bg-[var(--muted)]/40 p-3">
                <p className="text-xs font-medium uppercase text-[var(--muted-foreground)]">API Key</p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <code className="truncate font-mono text-sm">{revealed.apiKey}</code>
                  <CopyButton value={revealed.apiKey} />
                </div>
              </div>
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                <p className="text-xs font-medium uppercase text-amber-700">API Secret (shown once)</p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <code className="truncate font-mono text-sm">
                    {showSecret ? revealed.apiSecret : '••••••••••••••••••••••••••'}
                  </code>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setShowSecret((s) => !s)}
                      className="rounded-lg border border-[var(--border)] p-1.5 hover:bg-[var(--muted)]"
                      aria-label={showSecret ? 'Hide secret' : 'Show secret'}
                    >
                      {showSecret ? <EyeOff className="h-3.5 w-3.5" aria-hidden /> : <Eye className="h-3.5 w-3.5" aria-hidden />}
                    </button>
                    <CopyButton value={revealed.apiSecret} />
                  </div>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => { setRevealed(null); setShowSecret(false); }}
              className="mt-4 w-full rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-[var(--primary-foreground)] hover:opacity-90"
            >
              I have saved my secret
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
